import { AbstractControl, ValidationErrors } from '@angular/forms';
import { allForYear } from '@18f/us-federal-holidays';
import moment from 'moment';

export const HOLIDAY_NAMES = [
  `New Year's Day`,
  `Memorial Day`,
  `Independence Day`,
  `Labor Day`,
  `Thanksgiving Day`,
  `Christmas Day`,
];

function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function isObservedHoliday(
  calendarDate: Date,
  holidayNames: string[] = HOLIDAY_NAMES,
): boolean {
  const year = calendarDate.getFullYear();
  // Check adjacent years too: a Jan 1 that falls on a Saturday is observed
  // on Dec 31 of the previous year, which this widens the search to catch.
  const candidateHolidays = [
    ...allForYear(year - 1),
    ...allForYear(year),
    ...allForYear(year + 1),
  ];
  const targetDateString = toDateString(calendarDate);

  return candidateHolidays.some(
    (holiday) =>
      holidayNames.includes(holiday.name) &&
      holiday.dateString === targetDateString,
  );
}

export function isValidBusinessDateAfterDays(
  calendarDate: Date,
  days: number,
  holidayNames: string[] = HOLIDAY_NAMES,
): boolean {
  if (!calendarDate) return false;

  const day = calendarDate.getDay();
  const isWeekday = day !== 0 && day !== 6;
  const minimumDate = moment(new Date()).add(days, 'days');

  return (
    isWeekday &&
    !isObservedHoliday(calendarDate, holidayNames) &&
    calendarDate > minimumDate.toDate()
  );
}

export function getNextValidBusinessDate(
  days: number,
  holidayNames: string[] = HOLIDAY_NAMES,
): Date {
  const dateToCheck = moment(new Date()).add(days, 'days');

  while (
    !isValidBusinessDateAfterDays(dateToCheck.toDate(), days, holidayNames)
  ) {
    dateToCheck.add(1, 'days');
  }

  return dateToCheck.toDate();
}

export function createBusinessDateValidator(
  days: number,
  holidayNames: string[] = HOLIDAY_NAMES,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const formDate = control.value ? new Date(control.value) : null;
    const isValid = formDate
      ? isValidBusinessDateAfterDays(formDate, days, holidayNames)
      : false;

    return isValid ? null : { validateDate: { valid: false } };
  };
}
